import { ExtractFeatures, fromEnv, Keat } from 'keat';
import { ReactNode, useMemo } from 'react';
import { useAuth } from '~auth/useAuth';

declare module 'keat' {
  interface KeatNode {
    user: {
      email?: string;
      roles?: string[];
      permissions?: string[];
      organization?: string;
    };
  }
}

export type Feature = ExtractFeatures<typeof keat>;

export const keat = Keat.create({
  audiences: {
    // TODO: remove checks on roles after
    //  https://inthepocket.atlassian.net/browse/TPVWAVE-1082 is implemented
    staff: (user) =>
      (user?.roles?.includes('Staff') || user?.permissions?.includes('feature:staff')) ?? false,
    stakeholders: (user) =>
      (user?.roles?.includes('Stakeholder') ||
        user?.permissions?.includes('feature:stakeholder')) ??
      false,
    tester: (user) =>
      (user?.roles?.includes('Tester') || user?.permissions?.includes('feature:tester')) ?? false,
    tpvision: (user) => user?.organization === 'ce42e167-c6b5-4105-89f0-8290f3f559b2',
    // remove after ISE: https://inthepocket.atlassian.net/browse/TPVWAVE-1475
    futureAvSync: (user) => user?.organization === '7f43a84a-f1e5-40d1-9d02-1e21d0e24070',
    developer: (user) =>
      (user?.roles?.includes('Developer') || user?.permissions?.includes('feature:developer')) ??
      false,
  },
  features: {
    agentReleaseChannel: fromEnv(process.env.REACT_APP_ENABLE_AGENT_RELEASE_CHANNEL_TO, 'staff'),
    alerts: fromEnv(process.env.REACT_APP_ENABLE_ALERTS_TO, 'staff'),
    appDirectMarketplaceLink: fromEnv(
      process.env.REACT_APP_ENABLE_APPDIRECT_MARKETPLACE_LINK,
      'staff',
    ),
    apps: fromEnv(process.env.REACT_APP_ENABLE_APPS_TO, 'staff'),
    brightness: fromEnv(process.env.REACT_APP_ENABLE_BRIGHTNESS_TO, 'staff'),
    bulk: fromEnv(process.env.REACT_APP_ENABLE_BULK_TO, 'staff'),
    bulkFirmwareUpdate: fromEnv(process.env.REACT_APP_ENABLE_BULK_FW_UPDATE_TO, 'staff'),
    controls: fromEnv(process.env.REACT_APP_ENABLE_CONTROLS_TO, 'staff'),
    defaultContentSource: fromEnv(process.env.REACT_APP_ENABLE_DEFAULT_CONTENT_SOURCE_TO, 'staff'),
    ledStripColor: fromEnv(process.env.REACT_APP_ENABLE_LED_STRIP_COLOR_TO, 'staff'),
    orientation: fromEnv(process.env.REACT_APP_ENABLE_ORIENTATION_TO, 'staff'),
    playlist: fromEnv(process.env.REACT_APP_ENABLE_PLAYLIST_TO, 'staff'),
    portsControl: fromEnv(process.env.REACT_APP_ENABLE_PORTS_CONTROL_TO, 'staff'),
    powerSchedule: fromEnv(process.env.REACT_APP_ENABLE_POWER_SCHEDULE_TO, 'staff'),
    reboot: fromEnv(process.env.REACT_APP_ENABLE_REBOOT_TO, 'staff'),
    screenshot: fromEnv(process.env.REACT_APP_ENABLE_SCREENSHOT_TO, 'staff'),
    subscriptions: fromEnv(process.env.REACT_APP_ENABLE_SUBSCRIPTIONS_TO, 'staff'),
    timeZones: fromEnv(process.env.REACT_APP_ENABLE_TIME_ZONES_TO, 'staff'),
    usageDashboard: fromEnv(process.env.REACT_APP_ENABLE_USAGE_DASHBOARD_TO, 'staff'),
    usageDashboardClickable: fromEnv(
      process.env.REACT_APP_ENABLE_USAGE_DASHBOARD_CLICKABLE_TO,
      'staff',
    ),
    volume: fromEnv(process.env.REACT_APP_ENABLE_VOLUME_TO, 'staff'),
    volumeLimits: fromEnv(process.env.REACT_APP_ENABLE_VOLUME_LIMITS_TO, 'staff'),
  },
});

export function useFeatureFlag(feature: Feature) {
  const auth = useAuth();

  return useMemo(
    () => ({
      isEnabled: keat.isEnabled(feature, {
        email: auth.user.email,
        roles: auth.user['https://keat.io/audiences'],
        organization: auth.organization?.id ?? undefined,
        permissions: auth.user.permissions,
      }),
    }),
    [feature, auth],
  );
}

interface Props {
  feature: Feature;
  children: ReactNode;
}

export function FeatureFlag({ feature, children }: Props) {
  const { isEnabled } = useFeatureFlag(feature);

  if (!isEnabled) {
    return null;
  }

  return <>{children}</>;
}
